<script setup>
import { ref, reactive } from 'vue'
import { useVModel } from '@vueuse/core'
import { getList } from '@/api/modules/user'
import { getUser } from '@/api/user'


const $props = defineProps({
  modelValue: {
    type: [Number, String, Array],
    default: null
  },
  multiple: {
    type: Boolean,
    default: false
  }
})

const $emits = defineEmits(['update:modelValue'])

const value = useVModel($props, 'modelValue', $emits)

const userOptions = ref([])
const userList = ref([])
const loading = ref(false)
const scrollbarRef = ref()
const hasMore = ref(true)
const pagination = reactive({
  page: 1,
  size: 10,
  total: 0
})

const toUserOption = (user) => {
  const data = user?.data || user || {}
  return {
    id: data.id,
    name: data.displayName || data.name || data.username || String(data.id),
    type: 'user',
    leaf: true,
    avatar: data.avatarUrl || data.avatar,
    username: data.username
  }
}

const isSelected = (user) => {
  return userOptions.value.some((e) => String(e.id) === String(user.id))
}

const onUserClick = (data) => {
  if ($props.multiple) {
    const index = userOptions.value.findIndex((e) => String(e.id) === String(data.id))
    if (index === -1) {
      userOptions.value.push(data)
      userOptions.value.sort((a, b) => String(a.id).localeCompare(String(b.id)))
    } else {
      userOptions.value.splice(index, 1)
    }
  } else {
    const index = userOptions.value.findIndex((e) => String(e.id) === String(data.id))
    if (index === -1) {
      userOptions.value = [data]
    } else {
      userOptions.value.splice(index, 1)
    }
  }
}
const dialogVisible = ref(false)
const queryForm = reactive({
  name: ''
})

const open = () => {
  dialogVisible.value = true
}
const fetchUsers = async ({ append = false } = {}) => {
  if (loading.value) return
  loading.value = true
  try {
    const keyword = queryForm.name?.trim()
    const res = await getList({
      page: pagination.page,
      size: pagination.size,
      username: keyword || undefined
    })
    if (res.success) {
      const records = res.data?.records || res.data || []
      const nextUsers = records.map(toUserOption)
      if (append) {
        const exists = new Set(userList.value.map(user => String(user.id)))
        userList.value = userList.value.concat(nextUsers.filter(user => !exists.has(String(user.id))))
      } else {
        userList.value = nextUsers
      }
      pagination.total = Number(res.total ?? res.data?.total ?? records.length)
      hasMore.value = pagination.total > 0
        ? userList.value.length < pagination.total
        : records.length === pagination.size
    }
  } finally {
    loading.value = false
  }
}

const loadSelectedUsers = async () => {
  // 处理已选中的用户ID，查找并填充用户信息
  let userIds = []
  if (Array.isArray(value.value)) {
    userIds.push(...value.value)
  } else if (value.value) {
    userIds.push(value.value)
  }

  if (userIds.length > 0) {
    const selected = await Promise.all(userIds.map(async id => {
      const currentPageUser = userList.value.find(u => String(u.id) === String(id))
      if (currentPageUser) return { ...currentPageUser }
      try {
        return toUserOption(await getUser(id))
      } catch (error) {
        console.error('加载用户信息失败:', error)
        return { id, name: String(id), type: 'user', leaf: true, avatar: '', username: '' }
      }
    }))
    userOptions.value = selected.filter(user => user.id)
  } else {
    userOptions.value = []
  }
}

const onOpen = async () => {
  pagination.page = 1
  hasMore.value = true
  userList.value = []
  await fetchUsers()
  await loadSelectedUsers()
}

const handleSearch = () => {
  pagination.page = 1
  hasMore.value = true
  userList.value = []
  fetchUsers()
}

const loadNextPage = () => {
  if (loading.value || !hasMore.value) return
  pagination.page += 1
  fetchUsers({ append: true })
}

const handleScroll = ({ scrollTop }) => {
  const wrap = scrollbarRef.value?.wrapRef
  if (!wrap) return
  const distanceToBottom = wrap.scrollHeight - wrap.clientHeight - scrollTop
  if (distanceToBottom <= 24) {
    loadNextPage()
  }
}

const handelConfirm = () => {
  if ($props.multiple) {
    value.value = userOptions.value.map((e) => e.id)
  } else {
    if (userOptions.value.length > 0) {
      value.value = userOptions.value[0].id
    } else {
      value.value = null
    }
  }
  dialogVisible.value = false
}
defineExpose({
  open
})
</script>

<template>
  <el-dialog
    v-model="dialogVisible"
    @open="onOpen"
    align-center
    draggable
    title="选择用户"
    width="30%"
    append-to-body
    destroy-on-close
  >
    <el-card shadow="never" class="org-card">
      <template #header>
        <el-input
          v-model="queryForm.name"
          placeholder="输入关键字进行查询"
          :style="{ width: '100%' }"
          suffix-icon="search"
          clearable
          @keyup.enter="handleSearch"
          @clear="handleSearch"
        >
          <template #append>
            <el-button @click="handleSearch">查询</el-button>
          </template>
        </el-input>
      </template>
      <el-scrollbar
        ref="scrollbarRef"
        v-loading="loading && userList.length === 0"
        tag="div"
        class="org-tree"
        @scroll="handleScroll"
      >
        <div
          v-for="user in userList"
          :key="user.id"
          class="user-row"
          :class="{ 'is-active': isSelected(user) }"
          @click="onUserClick(user)"
        >
          <div class="flex-center min-w-0">
            <el-avatar :size="25" :src="user.avatar">
              {{ user.name.charAt(0) }}
            </el-avatar>
            <span class="user-name">{{ user.name }}</span>
          </div>
          <el-icon class="is-selected">
            <Check />
          </el-icon>
        </div>
        <div v-if="loading && userList.length > 0" class="load-hint">加载中...</div>
        <div v-else-if="!hasMore && userList.length > 0" class="load-hint">已加载全部</div>
        <el-empty v-else-if="!loading && userList.length === 0" description="暂无用户" :image-size="72" />
      </el-scrollbar>
    </el-card>
    <template #footer>
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" @click="handelConfirm">确认</el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.el-card {
  background-color: transparent;

  :deep(.el-card__header) {
    padding: 10px !important;
  }

  :deep(.el-card__body) {
    padding: 0 !important;
  }
}

.org-tree {
  height: 270px;
  padding: 5px;
}

.user-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  padding: 0 10px;
  border-radius: 8px;
  cursor: pointer;

  &:hover {
    background-color: var(--el-fill-color-light);
  }

  &.is-active {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);

    .is-selected {
      display: block;
    }
  }
}

.user-name {
  min-width: 0;
  margin-left: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.is-selected {
  display: none;
  padding-right: 15px;
}

.load-hint {
  padding: 10px 0 6px;
  color: var(--el-text-color-secondary);
  font-size: 12px;
  line-height: 18px;
  text-align: center;
}
</style>
